import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { LOGO_URL } from "@/data/homeContent";
import { ArrowLeft, Upload, Loader2, Copy, Trash2, ExternalLink, FileText, Images } from "lucide-react";

const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export default function MediaLibrary() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/media")
      .then((res) => setFiles(res.data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load media"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const copyLink = async (f) => {
    try {
      await navigator.clipboard.writeText(fileUrl(f.url));
      toast.success("Link copied to clipboard");
    } catch {
      window.prompt("Copy this link:", fileUrl(f.url));
    }
  };

  const del = async (f) => {
    if (!window.confirm(`Delete "${f.filename}"? This cannot be undone.`)) return;
    setDeleting(f.id);
    try {
      await api.delete(`/admin/media/${f.id}`);
      toast.success("File deleted");
      setFiles((fs) => fs.filter((x) => x.id !== f.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf9f6]" data-testid="admin-media-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#800020]" data-testid="media-back-btn">
            <ArrowLeft className="h-4 w-4" /> Back to Dashboard
          </Link>
          <img src={LOGO_URL} alt="BSD" className="h-8" />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif text-3xl text-slate-900">Media Library</h1>
            <p className="text-slate-500 text-sm mt-1">Upload images or files, then copy their public link to use anywhere on the website.</p>
          </div>
          <label className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#800020] hover:bg-[#660019] text-white font-medium cursor-pointer transition-colors" data-testid="media-upload-label">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Uploading…" : "Upload File"}
            <input type="file" accept="image/*,.pdf" onChange={upload} className="hidden" data-testid="media-upload-input" />
          </label>
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
        ) : files.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-14 text-center text-slate-400" data-testid="media-empty">
            <Images className="h-8 w-8 mx-auto mb-3 text-slate-300" />
            No files uploaded yet. Use the Upload File button to add your first image.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {files.map((f) => {
              const isImage = (f.content_type || "").startsWith("image/");
              return (
                <div key={f.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col" data-testid={`media-card-${f.id}`}>
                  <div className="h-44 bg-slate-50 border-b border-slate-100 flex items-center justify-center overflow-hidden">
                    {isImage ? (
                      <img src={fileUrl(f.url)} alt={f.filename} className="h-full w-full object-contain" />
                    ) : (
                      <FileText className="h-10 w-10 text-slate-300" />
                    )}
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <p className="text-sm font-medium text-slate-800 truncate" title={f.filename}>{f.filename}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{fmtSize(f.size || 0)}</p>
                    <div className="mt-3 flex items-center gap-2">
                      <button onClick={() => copyLink(f)} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[#800020] hover:bg-[#660019] text-white text-xs font-medium transition-colors" data-testid={`media-copy-${f.id}`}>
                        <Copy className="h-3.5 w-3.5" /> Copy Link
                      </button>
                      <a href={fileUrl(f.url)} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:border-[#800020] hover:text-[#800020] transition-colors" title="Open file" data-testid={`media-open-${f.id}`}>
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                      <button onClick={() => del(f)} disabled={deleting === f.id} className="p-2 rounded-lg border border-slate-200 text-red-500 hover:border-red-300 hover:bg-red-50 transition-colors disabled:opacity-50" title="Delete file" data-testid={`media-delete-${f.id}`}>
                        {deleting === f.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
